import { Link } from "react-router-dom";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { pricingTheme, type PricingTone } from "./pricingTheme";
import { PricingSectionHeader, formatCredits } from "./PricingSectionParts";
import { CHARGE_LABEL, UNIT_SUFFIX, type PublicRateCard } from "./rateCardTypes";

interface Props {
    tone: PricingTone;
    card: PublicRateCard;
}

/** Hidden until credits apply beyond AI voice minutes, which is when the rate card starts carrying burns. */
export default function CreditsExplainer({ tone, card }: Props) {
    const t = pricingTheme(tone);
    if (!card.credits || card.credits.length === 0) return null;

    return (
        <section aria-labelledby="pricing-credits" className={t.section}>
            <PricingSectionHeader
                tone={tone}
                icon={SparklesIcon}
                id="pricing-credits"
                title="Prepaid credits"
                subtitle="One credit is one AI voice minute. Credits are used before anything is charged, across every kind of usage."
            />
            <div className={`overflow-x-auto rounded-lg border ${t.divider}`}>
                <table className={`min-w-full divide-y ${t.divider}`}>
                    <caption className="sr-only">Credits used per charge</caption>
                    <tbody className={`divide-y ${t.divider}`}>
                        {card.credits.map((burn) => (
                            <tr key={burn.chargeType}>
                                <th scope="row" className={`px-4 py-3 text-left text-sm font-medium ${t.subheading}`}>
                                    {CHARGE_LABEL[burn.chargeType]}
                                </th>
                                <td className={`px-4 py-3 text-right text-sm tabular-nums ${t.heading}`}>
                                    <span className="font-bold">{formatCredits(burn.credits)}</span>
                                    <span className={`ml-1 text-xs ${t.muted}`}>
                                        {burn.credits === 1 ? "credit" : "credits"} {UNIT_SUFFIX[burn.unit]}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className={`mt-4 text-xs ${t.muted}`}>
                Credits come with an invoiced plan or as a grant from our team.{" "}
                <Link to="/contact" className={`font-semibold ${t.link}`}>
                    Ask about credits
                </Link>
            </p>
        </section>
    );
}
